/**
 * useCheckoutSubmit Hook
 * Handle submit order logic (upload files, patch data, submit)
 */

import { type AlertStatus, useToast } from '@chakra-ui/react';
import { PatchDataWrapper, PostDataWrapper } from '@diamond/shared/data-access';
import type { PaymentSubState } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  apiInstance,
  buyerCaptureException,
  LIST_FEATURE_FLAGS,
  trackEvent,
  useFeatureFlagWithState,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import type {
  CheckoutFormData,
  EcommerceItem,
  SubmitOrderPayload,
} from '@diamond/sol-buyer/utils';
import {
  decideCheckoutSuccessNavigation,
  formatPaymentSubStateLabel,
  formatProductsForTracking,
  handleCheckoutError,
  handleCheckoutSuccess,
  isCheckoutPaymentAvailabilityError,
  waitForPaymentInstructionFlag,
} from '@diamond/sol-buyer/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';

import { getOrderDetail } from '../../queries/order';
import { useAuthStore } from '../../stores/useAuthStore';
import { useCartStore } from '../../stores/useCartStore';
import { getOrderDetailQueryKey } from '../useGetOrderDetail';

const normalizePhoneNumber = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

export interface UseCheckoutSubmitReturn {
  submitOrder: (
    formData: CheckoutFormData,
    voucherCode?: string,
    voucherTitle?: string
  ) => Promise<void>;
  isSubmitting: boolean;
  submitError: string | null;
}

/**
 * Custom hook untuk handle checkout submission
 *
 * @example
 * const { submitOrder, isSubmitting } = useCheckoutSubmit(metaId, warehouse);
 */
export const useCheckoutSubmit = (
  metaId: string,
  warehouse: string
): UseCheckoutSubmitReturn => {
  const router = useRouter();
  const toast = useToast();
  const queryClient = useQueryClient();
  const paymentInstructionFlag = useFeatureFlagWithState(
    LIST_FEATURE_FLAGS.FF_PAYMENT_INSTRUCTION_PAGE,
    false
  );
  const paymentInstructionFlagRef = useRef(paymentInstructionFlag);
  paymentInstructionFlagRef.current = paymentInstructionFlag;
  const { access_token: token } = useAuthStore();
  const cartStore = useCartStore();

  const capturePaymentRouteFailure = (
    error: unknown,
    reason: string,
    paymentSubState?: PaymentSubState
  ) => {
    buyerCaptureException(
      {
        error: error instanceof Error ? error.message : String(error),
        message: 'Checkout payment destination resolution failed',
        reason,
      },
      (scope) => {
        scope.setTag('feature', 'payment_instruction');
        scope.setTag('source', 'checkout');
        scope.setTag('payment_instruction', reason);
        scope.setContext('payment_instruction', {
          order_meta_id: metaId,
          payment_sub_state:
            paymentSubState === undefined
              ? undefined
              : formatPaymentSubStateLabel(paymentSubState),
          reason,
          source: 'checkout',
          warehouse,
        });
      }
    );
  };

  const navigateCheckoutSuccess = async (
    formData: CheckoutFormData
  ): Promise<void> => {
    const flagWait = await waitForPaymentInstructionFlag(
      () => paymentInstructionFlagRef.current
    );

    let detailOutcome:
      | { status: 'ok'; paymentSubState: PaymentSubState }
      | { status: 'failed' } = { status: 'failed' };

    const shouldFetchDetail =
      formData.payment_method === 'bank_transfer' &&
      (flagWait.status === 'timed_out' ||
        flagWait.status === 'error' ||
        (flagWait.status === 'ready' && flagWait.value));

    if (shouldFetchDetail) {
      try {
        const submittedOrder = await getOrderDetail(token, metaId, warehouse);
        queryClient.setQueryData(
          getOrderDetailQueryKey(metaId, warehouse),
          submittedOrder
        );
        detailOutcome = {
          status: 'ok',
          paymentSubState: submittedOrder.payment_sub_state,
        };
      } catch {
        detailOutcome = { status: 'failed' };
      }
    }

    const decision = decideCheckoutSuccessNavigation({
      paymentMethod: formData.payment_method,
      flagWait,
      orderMetaId: metaId,
      warehouse,
      detailOutcome,
    });

    if (decision.reportReason) {
      capturePaymentRouteFailure(
        new Error(decision.reportReason),
        decision.reportReason,
        decision.paymentSubState
      );
    }

    if (decision.shouldTrackWfpResolved) {
      trackEvent(TRACKING_EVENTS.WFP_ROUTE_RESOLVED, {
        entry_source: 'checkout',
        ff_payment_instruction_page: true,
        order_meta_id: metaId,
        payment_method: formData.payment_method,
        payment_sub_state: decision.paymentSubState,
        platform: 'web',
        presentation: 'page',
        resolved_destination: decision.kind,
        source: 'checkout',
        sub_state: decision.paymentSubState,
        warehouse,
      });
    }

    switch (decision.kind) {
      case 'payment_instruction':
      case 'order_detail_proof':
      case 'order_detail':
      case 'unpaid_orders':
        await router.replace(decision.href);
        return;
      default: {
        const _exhaustive: never = decision.kind;
        throw new Error(`Unhandled checkout destination: ${_exhaustive}`);
      }
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { patchData } = PatchDataWrapper('/order/meta', token, 'buyer');
  const { postData } = PostDataWrapper(
    '/order/purchase-order-document/upload',
    token,
    true,
    'buyer'
  );

  // Wrapper function to match ToastFunction signature
  const toastFn = (
    _toast: ReturnType<typeof useToast>,
    status: AlertStatus,
    message: string
  ) => {
    showToast(toast, status, message);
  };

  /**
   * Upload PO documents
   */
  const uploadDocuments = async (files: File[]): Promise<string[]> => {
    if (!files || files.length === 0) return [];

    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`file-${index}`, file, file.name);
    });

    try {
      const config = {
        headers: {
          authorization: `Bearer ${token}`,
        },
      };

      const response = await postData(formData, config);
      return (response as { urls?: string[] })?.urls || [];
    } catch (error: unknown) {
      handleCheckoutError(
        error,
        'upload-document',
        toastFn,
        toast,
        'Gagal upload dokumen',
        metaId
      );
      throw error;
    }
  };

  /**
   * Patch order data (update delivery info, payment method, etc.)
   */
  const patchOrderData = async (
    formData: CheckoutFormData,
    documentUrls: string[]
  ): Promise<void> => {
    try {
      await patchData(
        {
          purchase_order_docs: documentUrls,
          delivery_date: formData.delivery_date,
          delivery_note: formData.delivery_note,
          delivery_option: formData.delivery_option,
          payment_method: formData.payment_method,
          ship_to_contact: {
            name: formData.ship_to_contact.name,
            telp: normalizePhoneNumber(formData.ship_to_contact.telp),
          },
        },
        metaId
      );
    } catch (error: unknown) {
      handleCheckoutError(
        error,
        'patch-order',
        toastFn,
        toast,
        'Gagal update order',
        metaId
      );
      throw error;
    }
  };

  /**
   * Submit order to backend
   */
  const doSubmitOrder = async (
    payload: SubmitOrderPayload,
    formData: CheckoutFormData,
    voucherCode?: string,
    voucherTitle?: string
  ): Promise<void> => {
    try {
      const response = await apiInstance(token).post(
        `/order/meta/${metaId}/submit`,
        payload
      );

      if (response.status === 200 || response.status === 201) {
        // Reset cart
        cartStore.reset([]);

        // Prepare tracking items
        const trackingItems: EcommerceItem[] = [];
        if (formData.items?.length > 0) {
          formData.items.forEach((item) => {
            const formatted = formatProductsForTracking(
              item.products,
              warehouse
            );
            trackingItems.push(...formatted);
          });
        }

        // Track purchase event
        trackEvent(TRACKING_EVENTS.PURCHASE, {
          transaction_id: formData.transaction_id,
          currency: 'IDR',
          value: formData.total || 0,
          total_discount: formData.saved_total || 0,
          items: trackingItems,
          section_name: TRACKING_PAGES.CHECKOUT,
        });

        // Track submit_voucher alongside purchase
        if (voucherCode) {
          trackEvent(TRACKING_EVENTS.SUBMIT_VOUCHER, {
            voucher_id: voucherCode,
            voucher_name: voucherTitle ?? voucherCode,
            currency: 'IDR',
            value: formData.total || 0,
            page_name: TRACKING_PAGES.CHECKOUT,
            section_name: TRACKING_PAGES.CHECKOUT,
          });
        }

        // Track payment info event
        trackEvent(TRACKING_EVENTS.ADD_PAYMENT_INFO, {
          currency: 'IDR',
          value: formData.total || 0,
          payment_type: formData.payment_method,
          warehouse,
          meta_id: metaId,
          items: trackingItems,
          section_name: TRACKING_PAGES.CHECKOUT,
        });

        // Show success message
        handleCheckoutSuccess(toastFn, toast, 'Berhasil Checkout!');
        await navigateCheckoutSuccess(formData);
      }
    } catch (error: unknown) {
      handleCheckoutError(
        error,
        'submit-order',
        toastFn,
        toast,
        'Gagal submit order',
        metaId
      );
      if (!isCheckoutPaymentAvailabilityError(error)) {
        router.push('/cart');
      }
      throw error;
    }
  };

  /**
   * Main submit function
   * @param formData - Checkout form data
   * @param voucherCode - Optional voucher code to apply (SAP voucher code)
   */
  const submitOrder = async (
    formData: CheckoutFormData,
    voucherCode?: string,
    voucherTitle?: string
  ): Promise<void> => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Upload documents (if any)
      let documentUrls: string[] = [];
      if (
        formData.purchase_order_document &&
        formData.purchase_order_document.length > 0
      ) {
        const files = Array.from(formData.purchase_order_document);
        documentUrls = await uploadDocuments(files);
      }

      // 2. Patch order data
      await patchOrderData(formData, documentUrls);

      // 3. Submit order
      const payload: SubmitOrderPayload = {
        id: metaId,
        warehouse,
        ...(voucherCode && { sap_voucher_code: voucherCode }),
      };
      await doSubmitOrder(payload, formData, voucherCode, voucherTitle);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Gagal submit order';
      setSubmitError(errorMessage);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitOrder,
    isSubmitting,
    submitError,
  };
};
